const { getOffsetTop } = window.BAO.Utils

export class StickyBenefits extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['header', 'indicator', 'number', 'trigger', 'benefit']
  }

  connectedCallback () {
    super.connectedCallback()

    this.isTicking = false
    this.lastKnownY = window.scrollY
    this.currentName = this.els.benefit.elements[0].dataset.name
    this.currentNumber = this.els.benefit.elements[0].dataset.number

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    this.updateEl()
    this.setHeaderHeight()

    if (this.hasAttribute('data-locked') && !window.BAO.MediaBreakpoints.isLg) {
      this.setLockedState()
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    this.onTriggerClick = this.onTriggerClick.bind(this)

    window.addEventListener('scroll', this.onScroll, { passive: true })

    this.els.trigger.elements.forEach(trigger => {
      this.listeners.add(trigger, 'click', this.onTriggerClick)
    })

    this.listeners.add(
      this.els.select.element,
      'click',
      this.onSelectClick.bind(this),
    )
    this.listeners.add(
      this.els.header.element,
      'click',
      this.onUnlockClick.bind(this),
    )
    this.listeners.add(
      this.els.unlock.element,
      'click',
      this.onUnlockClick.bind(this),
    )

    this.listeners.add(window, 'resize', this.onResize.bind(this))
  }

  onScroll () {
    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.updateBenefits()
  }

  updateBenefits () {
    if (!this.offsetHeight || this.isLocked) return

    const elementHeight = this.offsetHeight
    const elementStartPoint = getOffsetTop(this)
    const elementEndPoint =
      getOffsetTop(this) +
      elementHeight +
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--Grid_Gutter',
        ),
      )
    const bottomPos = this.lastKnownY + window.innerHeight

    const isAtStart = this.lastKnownY <= elementStartPoint
    const isAtEnd = bottomPos >= elementEndPoint

    if (isAtStart || isAtEnd) {
      const index = isAtStart ? 0 : this.els.benefit.elements.length - 1

      this.els.benefit.elements.forEach((benefit, i) => {
        benefit.setAttribute('aria-current', i === index ? 'true' : 'false')
      })

      this.currentName = this.els.benefit.elements[index].dataset.name
      this.currentNumber = this.els.benefit.elements[index].dataset.number
    } else {
      const middlePoint = window.scrollY + window.innerHeight / 2

      this.els.benefit.elements.forEach(benefit => {
        const benefitTop = getOffsetTop(benefit)
        const benefitBottom =
          benefitTop +
          benefit.offsetHeight +
          parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue(
              '--Grid_Gutter',
            ),
          )

        benefit.setAttribute(
          'aria-current',
          middlePoint > benefitTop && middlePoint < benefitBottom
            ? 'true'
            : 'false',
        )

        if (middlePoint > benefitTop && middlePoint < benefitBottom) {
          this.currentName = benefit.dataset.name
          this.currentNumber = benefit.dataset.number
        }
      })
    }

    this.els.trigger.elements.forEach(trigger => {
      trigger.setAttribute(
        'aria-current',
        trigger.dataset.number === this.currentNumber ? 'true' : 'false',
      )
    })

    this.els.indicator.element.textContent = this.currentName
    this.els.number.element.textContent = this.currentNumber
  }

  onTriggerClick (event) {
    const targetBenefit = this.els.benefit.elements.find(
      benefit => benefit.dataset.number === event.currentTarget.dataset.number,
    )

    if (targetBenefit) {
      const index = this.els.benefit.elements.indexOf(targetBenefit)
      const isFirstOrSecond = index === 0 || index === 1
      const isLastOrSecondLast =
        index === this.els.benefit.elements.length - 1 ||
        index === this.els.benefit.elements.length - 2
      const blockPosition = window.BAO.MediaBreakpoints.isLg
        ? isFirstOrSecond
          ? 'start'
          : isLastOrSecondLast
            ? 'end'
            : 'center'
        : index === 0
          ? 'start'
          : index === this.els.benefit.elements.length - 1
            ? 'end'
            : 'start'

      targetBenefit.scrollIntoView({ behavior: 'smooth', block: blockPosition })

      this.els.header.element.setAttribute('aria-expanded', 'false')
    }
  }

  onSelectClick () {
    this.els.header.element.setAttribute(
      'aria-expanded',
      this.els.header.element.getAttribute('aria-expanded') === 'true'
        ? 'false'
        : 'true',
    )
  }

  onUnlockClick (e) {
    if (!this.hasAttribute('data-locked')) return

    this.removeAttribute('data-locked')

    if (e.target.dataset.stickyBenefitsEl === 'header') {
      this.els.header.element.setAttribute('aria-expanded', 'true')
    }

    this.listeners.remove(this.els.header.element, 'click', this.onUnlockClick)
    this.listeners.remove(this.els.unlock.element, 'click', this.onUnlockClick)
  }

  onResize () {
    this.setLockedState()
    this.setHeaderHeight()
  }

  setLockedState () {
    if (!this.hasAttribute('data-locked')) return

    this.setAttribute(
      'data-locked',
      window.BAO.MediaBreakpoints.isLg ? 'false' : 'true',
    )
  }

  setHeaderHeight () {
    const headerHeight = this.els.header.element.offsetHeight
    this.style.setProperty(
      '--Sticky_Benefits_Header_Height',
      `${headerHeight}px`,
    )
  }
}

if (!customElements.get('sticky-benefits')) {
  customElements.define('sticky-benefits', StickyBenefits)
}
